import { can } from '@clim/client-core';
import {
  STOCK_MOVEMENT_LABELS,
  STOCK_RESERVE_STAGES,
  STOCK_RESERVE_STAGE_LABELS,
  UNIT_LABELS,
  addDays,
  isoDate,
  type PickingList,
  type StockIndicator,
  type StockLevel,
  type StockMovementKind,
  type StockReserveStage,
} from '@clim/domain';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StockChip } from '../components/DossierLines';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

type Row = StockLevel & { label: string; model: string | null; unit: keyof typeof UNIT_LABELS; territoryCode: string; indicator: StockIndicator };
interface Movement {
  id: string;
  kind: StockMovementKind;
  quantity: number;
  onHandAfter: number;
  reservedAfter: number;
  comment: string | null;
  at: string;
  dossierId: string | null;
  reference: string | null;
  by: string;
}

const qty = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

/**
 * Stock et matériel (§13) : niveaux par territoire (en stock, réservé, disponible, sorti, seuil),
 * entrées et ajustements motivés, historique des mouvements, stade de réservation, et matériel
 * à préparer par journée. Le stock ne bloque jamais une vente : un disponible négatif est signalé.
 */
export function StockPage() {
  const { api, me } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const manage = can(me, 'stock.manage');
  const [tab, setTab] = useState<'levels' | 'picking'>('levels');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [reserveAt, setReserveAt] = useState<StockReserveStage>('validated');
  const [filter, setFilter] = useState<'all' | 'alert'>('all');
  const [open, setOpen] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!online) return;
    api.request<{ settings: { reserveAt: StockReserveStage }; levels: Row[] }>('GET', `/v1/stock${territoryId ? `?territoryId=${territoryId}` : ''}`).then(
      (d) => (setRows(d.levels), setReserveAt(d.settings.reserveAt), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, online, territoryId]);
  useEffect(load, [load]);

  const saveStage = async (v: StockReserveStage) => {
    try {
      await api.request('PATCH', '/v1/stock/settings', { reserveAt: v });
      setReserveAt(v);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const shown = (rows ?? []).filter((r) => filter === 'all' || r.indicator !== 'available');
  const alerts = (rows ?? []).filter((r) => r.indicator !== 'available').length;

  return (
    <div>
      <h1>Stock et matériel</h1>
      <div className="chips" role="tablist">
        <button role="tab" aria-selected={tab === 'levels'} className={`chip${tab === 'levels' ? ' on' : ''}`} onClick={() => setTab('levels')}>
          Niveaux{alerts ? ` · ${alerts} alerte(s)` : ''}
        </button>
        <button role="tab" aria-selected={tab === 'picking'} className={`chip${tab === 'picking' ? ' on' : ''}`} onClick={() => setTab('picking')}>
          Matériel à préparer
        </button>
      </div>
      {!online && <Empty>Le stock nécessite une connexion.</Empty>}
      <ErrorBox message={error} />
      {tab === 'picking' ? (
        <Picking />
      ) : (
        <>
          <div className="filters">
            <Field label="Afficher">
              <Select value={filter} onChange={(e) => setFilter(e.target.value as 'all' | 'alert')}>
                <option value="all">Toutes les références stockées</option>
                <option value="alert">Stock faible ou indisponible</option>
              </Select>
            </Field>
            <Field label="Réservation automatique" hint="Le matériel est réservé pour le dossier à ce stade, libéré s’il est annulé ou renvoyé en correction, sorti à l’installation.">
              <Select value={reserveAt} disabled={!manage} onChange={(e) => void saveStage(e.target.value as StockReserveStage)}>
                {STOCK_RESERVE_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STOCK_RESERVE_STAGE_LABELS[s]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {rows && !shown.length && <Empty>Aucune référence « stockée ». Passez une référence en « stockée » dans le catalogue pour suivre son stock.</Empty>}
          {shown.length > 0 && (
            <table className="table" aria-label="Niveaux de stock">
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Territoire</th>
                  <th>En stock</th>
                  <th>Réservé</th>
                  <th>Disponible</th>
                  <th>Sorti</th>
                  <th>Seuil</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={`${r.itemId}:${r.territoryId}`} className="clickable" onClick={() => setOpen(r)}>
                    <td>
                      <strong>{r.label}</strong>
                      {r.model && <div className="muted">{r.model}</div>}
                    </td>
                    <td>{r.territoryCode}</td>
                    <td>{qty(r.onHand)}</td>
                    <td>{qty(r.reserved)}</td>
                    <td>
                      <strong>{qty(r.available)}</strong>
                    </td>
                    <td>{qty(r.issued)}</td>
                    <td>{qty(r.threshold)}</td>
                    <td>
                      <StockChip indicator={r.indicator} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {open && <LevelDialog row={open} manage={manage} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function LevelDialog({ row, manage, onClose, onChanged }: { row: Row; manage: boolean; onClose(): void; onChanged(): void }) {
  const { api } = useReady();
  const [level, setLevel] = useState<StockLevel>(row);
  const [history, setHistory] = useState<Movement[]>([]);
  const [kind, setKind] = useState<'receipt' | 'adjustment'>('receipt');
  const [quantity, setQuantity] = useState('');
  const [comment, setComment] = useState('');
  const [threshold, setThreshold] = useState(String(row.threshold));
  const [error, setError] = useState<string | null>(null);
  const loadHistory = useCallback(() => {
    api.request<Movement[]>('GET', `/v1/stock/movements?itemId=${row.itemId}&territoryId=${row.territoryId}`).then(setHistory, (e) => setError(errorMessage(e)));
  }, [api, row.itemId, row.territoryId]);
  useEffect(loadHistory, [loadHistory]);

  const submit = async () => {
    setError(null);
    try {
      const l = await api.request<StockLevel>('POST', '/v1/stock/movements', { itemId: row.itemId, territoryId: row.territoryId, kind, quantity: Number(quantity.replace(',', '.')), comment: comment || null });
      setLevel(l);
      setQuantity('');
      setComment('');
      loadHistory();
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const saveThreshold = async () => {
    setError(null);
    try {
      const l = await api.request<StockLevel>('PATCH', '/v1/stock/threshold', { itemId: row.itemId, territoryId: row.territoryId, threshold: Number(threshold.replace(',', '.')) });
      setLevel(l);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Modal title={`${row.label} — ${row.territoryCode}`} onClose={onClose}>
      <dl className="money" aria-label="Niveau">
        <div>
          <dt>En stock</dt>
          <dd>{qty(level.onHand)}</dd>
        </div>
        <div>
          <dt>Réservé</dt>
          <dd>{qty(level.reserved)}</dd>
        </div>
        <div className="balance">
          <dt>Disponible</dt>
          <dd>{qty(level.available)}</dd>
        </div>
      </dl>
      {manage && (
        <div className="form">
          <div className="row-inline">
            <Field label="Mouvement">
              <Select value={kind} onChange={(e) => setKind(e.target.value as 'receipt' | 'adjustment')}>
                <option value="receipt">Entrée (réception)</option>
                <option value="adjustment">Ajustement (+ / −)</option>
              </Select>
            </Field>
            <Field label="Quantité" hint={kind === 'adjustment' ? 'Négatif pour retirer (casse, inventaire…)' : undefined}>
              <Input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} aria-label="Quantité" />
            </Field>
          </div>
          <Field label={kind === 'adjustment' ? 'Motif (obligatoire)' : 'Commentaire'}>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} aria-label="Motif" />
          </Field>
          <button className="btn primary" onClick={() => void submit()} disabled={!quantity}>
            Enregistrer le mouvement
          </button>
          <div className="row-inline">
            <Field label="Seuil d’alerte">
              <Input inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} aria-label="Seuil d’alerte" />
            </Field>
            <button className="btn" onClick={() => void saveThreshold()}>
              Enregistrer le seuil
            </button>
          </div>
        </div>
      )}
      <ErrorBox message={error} />
      <h2>Historique</h2>
      {!history.length ? (
        <Empty>Aucun mouvement.</Empty>
      ) : (
        <table className="table compact" aria-label="Historique des mouvements">
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td className="nowrap">{new Date(h.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td>
                  {STOCK_MOVEMENT_LABELS[h.kind]} {h.quantity > 0 && h.kind !== 'release' && h.kind !== 'issue' ? '+' : h.kind === 'release' || h.kind === 'issue' ? '−' : ''}
                  {qty(Math.abs(h.quantity))}
                  {h.dossierId && (
                    <>
                      {' · '}
                      <Link to={`/dossiers/${h.dossierId}`}>{h.reference ?? 'dossier'}</Link>
                    </>
                  )}
                  {h.comment && <div className="muted">{h.comment}</div>}
                </td>
                <td className="nowrap muted">
                  {h.by} · stock {qty(h.onHandAfter)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

function Picking() {
  const { api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const [day, setDay] = useState(addDays(isoDate(new Date()), 1));
  const [data, setData] = useState<PickingList | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!online) return;
    api.request<PickingList>('GET', `/v1/stock/picking?date=${day}${territoryId ? `&territoryId=${territoryId}` : ''}`).then(
      (d) => (setData(d), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, day, online, territoryId]);
  return (
    <div>
      <div className="filters">
        <Field label="Journée">
          <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
        </Field>
        <button className="btn" onClick={() => window.print()}>
          Imprimer
        </button>
      </div>
      <ErrorBox message={error} />
      {data && !data.interventions.length && <Empty>Aucune intervention planifiée ce jour-là.</Empty>}
      {data && data.interventions.length > 0 && (
        <>
          <div className="card">
            <h2>Total à préparer</h2>
            <ul className="plain" aria-label="Total à préparer">
              {data.totals.map((t) => (
                <li key={t.itemId}>
                  <strong>{qty(t.quantity)}</strong> {UNIT_LABELS[t.unit as keyof typeof UNIT_LABELS] ?? t.unit} — {t.label}
                  {!t.stocked && <span className="muted"> (non suivi en stock)</span>}
                </li>
              ))}
            </ul>
          </div>
          {data.interventions.map((i) => (
            <div className="card" key={i.interventionId}>
              <h2>
                {i.technicianName ?? 'Non attribuée'} <span className="muted">· {[i.slot, i.city, i.reference].filter(Boolean).join(' · ')}</span>
              </h2>
              <ul className="plain">
                {i.lines.map((l) => (
                  <li key={l.itemId}>
                    {qty(l.quantity)} × {l.label}
                  </li>
                ))}
              </ul>
              <Link to={`/dossiers/${i.dossierId}`}>Ouvrir le dossier</Link>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
